import { Injectable, NgZone } from '@angular/core';
import { TourBuilder, TourStep } from '../tour.types';
import { Buttons } from '../tour.buttons';
import { waitForElement } from '../tour.utils';
import { Router } from '@angular/router';
import { TourIntentService } from '../tour-intent.service';
import { APP_ROUTES } from '@core/constants';

@Injectable()
export class ProfileTourBuilder implements TourBuilder {
  key = 'profile';

  // Per-tour defaults merged by TourService
  defaults: Partial<TourStep> = {
    scrollTo: { behavior: 'smooth', block: 'center' } as const,
    arrow: true,
    modalOverlayOpeningPadding: 3
  };

  constructor(private router: Router, private zone: NgZone) {}

  buildSteps(): TourStep[] {
    const router = this.router;
    const zone = this.zone;

    return [
      {
        id: 'welcome-profile-preferences',
        skipOnResume: true,
        text: `
          <img class="tour-img" src="/assets/tours/profile-preferences.svg" alt="profile setup wizard" loading="lazy"/>
          <h2 class="tour-title">Welcome to Your Profile!</h2>
          <div class="tour-text">
            Your profile lets others on Impact Tracker and TrackImpact know you. We'll guide you through quick steps to keep it accurate.
          </div>
        `,
        classes: 'shepherd-theme-dialog',
        buttons: [Buttons.next(`Let's begin`)]
      },
      {
        id: 'profile-title',
        attachTo: { element: '[data-tour-id="profile-title"]', on: 'right' },
        text: '<p>Start by selecting your preferred <strong>Title</strong> from the list.</p>',
        classes: 'shepherd-theme-step',
        highlightClass: 'tour-highlight-pulse-light',
        scrollTo: false,
        modalOverlayOpeningPadding: 3,
        when: {
          show: function () {
            const el = document.querySelector(
              '[data-tour-id="profile-title"]'
            ) as HTMLElement | null;
            el?.classList.add('tour-highlight-pulse');
            const select = el?.querySelector('select') as HTMLSelectElement | null;

            if (select && !select.value) {
              select.focus();
            }
          },
          hide: function () {
            document
              .querySelector('[data-tour-id="profile-title"]')
              ?.classList.remove('tour-highlight-pulse');
          }
        },
        buttons: [Buttons.next('Next')]
      },
      {
        id: 'profile-alt-emails',
        attachTo: { element: '[data-tour-id="profile-alt-emails"]', on: 'top' },
        classes: 'shepherd-theme-step',
        highlightClass: 'tour-highlight-pulse-light',
        scrollTo: true,
        canClickTarget: true,
        modalOverlayOpeningPadding: 3,
        arrow: true,
        text: `
          <p>Add any <strong>alternate email addresses</strong> you use. These help with recovery and notifications.</p>
          <ul class="tour-bullets">
            <li>Use a valid format (e.g., name@domain.com).</li>
            <li>You can add multiple addresses via <em>Add Email</em>.</li>
            <li>Remove one later with the “×” next to it.</li>
          </ul>
        `,
        beforeShowPromise: () =>
          new Promise<void>((resolve, reject) => {
            const deadline = performance.now() + 2000;
            const waitForAltEmailsGroup = () => {
              const group = document.querySelector(
                '[data-tour-id="profile-alt-emails"]'
              ) as HTMLElement | null;

              if (group) {
                group.classList.add('tour-highlight-pulse');
                // focus the input if available
                const input = group.querySelector(
                  'input#emails[formcontrolname="addEmail"]'
                ) as HTMLInputElement | null;
                input?.focus();
                group.scrollIntoView({ behavior: 'smooth', block: 'center' });
                resolve();
                return;
              }

              if (performance.now() > deadline) {
                reject(new Error('alt emails group not found'));
                return;
              }
              setTimeout(waitForAltEmailsGroup, 50);
            };
            waitForAltEmailsGroup();
          }),

        when: {
          hide: () => {
            const group = document.querySelector(
              '[data-tour-id="profile-alt-emails"]'
            ) as HTMLElement | null;
            group?.classList.remove('tour-highlight-pulse');
          }
        },

        buttons: [Buttons.next('Next')]
      },

      {
        id: 'profile-organisation',
        attachTo: { element: '[data-tour-id="profile-organisation"]', on: 'bottom' },
        text: '<p>Enter your <strong>Organisation</strong>. This is required for Impact Tracker and lets others see your affiliation.</p>',
        classes: 'shepherd-theme-step',
        highlightClass: 'tour-highlight-pulse',
        scrollTo: false,
        when: {
          show: function () {
            const el = document.querySelector(
              '[data-tour-id="profile-organisation"]'
            ) as HTMLElement | null;
            el?.classList.add('tour-highlight-pulse');

            const input = el?.querySelector('input') as HTMLInputElement | null;
            if (input && !input.value) {
              input.focus();
            }
          },
          hide: function () {
            document
              .querySelector('[data-tour-id="profile-organisation"]')
              ?.classList.remove('tour-highlight-pulse');
          }
        },
        buttons: [Buttons.next('Next')]
      },
      {
        id: 'profile-job-title',
        attachTo: { element: '[data-tour-id="profile-job-title"]', on: 'bottom' },
        text: '<p>Type your current <strong>Job title</strong> so others know your role and area of expertise.</p>',
        classes: 'shepherd-theme-step',
        highlightClass: 'tour-highlight-pulse',
        scrollTo: false,
        when: {
          show: function () {
            const el = document.querySelector(
              '[data-tour-id="profile-job-title"]'
            ) as HTMLElement | null;
            el?.classList.add('tour-highlight-pulse');
            const input = el?.querySelector('input') as HTMLInputElement | null;

            if (input && !input.value) {
              input.focus();
            }
          },
          hide: function () {
            document
              .querySelector('[data-tour-id="profile-job-title"]')
              ?.classList.remove('tour-highlight-pulse');
          }
        },
        buttons: [Buttons.next('Next')]
      },
      {
        id: 'profile-location',
        attachTo: { element: '[data-tour-id="profile-location"]', on: 'right' },
        text: '<p>Choose your <strong>Location</strong>. This helps tailor your experience.</p>',
        classes: 'shepherd-theme-step',
        highlightClass: 'tour-highlight-pulse',
        scrollTo: false,
        when: {
          show: function () {
            const el = document.querySelector(
              '[data-tour-id="profile-location"]'
            ) as HTMLElement | null;
            el?.classList.add('tour-highlight-pulse');
            const select = el?.querySelector('select') as HTMLSelectElement | null;

            if (select && !select.value) {
              select.focus();
            }
          },
          hide: function () {
            document
              .querySelector('[data-tour-id="profile-location"]')
              ?.classList.remove('tour-highlight-pulse');
          }
        },
        buttons: [Buttons.next('Next')]
      },
      {
        id: 'complete-profile-update',
        attachTo: { element: '[data-tour-id="profile-update-button"]', on: 'top' },
        text: `<p>When you've filled out all the details, click <strong>Update</strong> to save your profile.</p>`,
        canClickTarget: true,
        classes: 'shepherd-theme-step',
        highlightClass: 'tour-highlight-pulse',
        scrollTo: true,
        when: {
          show: function () {
            const btn = document.querySelector(
              '[data-tour-id="profile-update-button"]'
            ) as HTMLElement | null;
            btn?.classList.add('tour-highlight-pulse');
            setTimeout(() => btn?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
          },
          hide: function () {
            document
              .querySelector('[data-tour-id="profile-update-button"]')
              ?.classList.remove('tour-highlight-pulse');
          }
        },
        buttons: [Buttons.next('Next')]
      },
      {
        id: 'go-to-about-me',
        attachTo: { element: '[data-tour-id="profile-about-me"]', on: 'right' },
        text: `<p>In the <strong>About Me</strong> section, you can share your biography, expertise, and areas of interest.`,
        canClickTarget: true,
        classes: 'shepherd-theme-step',
        highlightClass: 'tour-highlight-pulse-light',
        scrollTo: false,
        when: {
          show: function () {
            const tab = document.querySelector(
              '[data-tour-id="profile-about-me"]'
            ) as HTMLElement | null;
            tab?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        },
        buttons: [
          {
            text: 'Next',
            classes: 'shepherd-btn shepherd-btn-primary',
            action: function (this: any) {
              const tour = this;
              const tab = document.querySelector(
                '[data-tour-id="profile-about-me"]'
              ) as HTMLElement | null;
              tab?.click();
              waitForElement('[data-tour-id="profile-expertise"]')
                .then(el => el.scrollIntoView({ behavior: 'smooth', block: 'center' }))
                .catch(() => {})
                .finally(() => tour.next());
            }
          }
        ]
      },
      {
        id: 'go-to-social-networks',
        attachTo: { element: '[data-tour-id="profile-social-network"]', on: 'right' },
        text: `<p>In the <strong>Social Networks </strong> section lets you link your professional and academic profiles, making it easier for others to connect with you.`,
        canClickTarget: true,
        classes: 'shepherd-theme-step',
        highlightClass: 'tour-highlight-pulse-light',
        scrollTo: false,
        when: {
          show: function () {
            const tab = document.querySelector(
              '[data-tour-id="profile-social-network"]'
            ) as HTMLElement | null;
            tab?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        },
        buttons: [
          {
            text: 'Next',
            classes: 'shepherd-btn shepherd-btn-primary',
            action: function (this: any) {
              const tour = this;
              const tab = document.querySelector(
                '[data-tour-id="profile-social-network"]'
              ) as HTMLElement | null;
              tab?.click();
              waitForElement('[data-tour-id="nav-dashboard"]')
                .catch(() => {})
                .finally(() => tour.next());
            }
          }
        ]
      },
      {
        id: 'profile-back-to-dashboard-nav',
        attachTo: { element: '[data-tour-id="nav-dashboard"]', on: 'left' },
        text: `
          <img class="tour-img" src="/assets/tours/profile-complete2.svg" alt="profile setup wizard" loading="lazy"/>
          <h2 class="tour-title">🎉 Profile onboarding complete!</h2>
          <div class="tour-text">
            Head to the Dashboard — your central hub for accessing HIVVE products and tools.
          </div>
        `,
        classes: 'shepherd-theme-dialog arrow-nudge-go-profile',
        scrollTo: false,
        canClickTarget: true,
        highlightClass: 'tour-highlight-pulse-light',
        arrow: true,
        beforeShowPromise: () =>
          new Promise<void>((resolve, reject) => {
            const sel = '[data-tour-id="nav-dashboard"]';
            const end = performance.now() + 2000;
            (function check() {
              const el = document.querySelector(sel);
              if (el) return resolve();
              if (performance.now() > end) return reject(new Error('Dashboard nav not found'));
              setTimeout(check, 50);
            })();
          }),
        buttons: [
          {
            text: 'Go to Dashboard',
            classes: 'shepherd-btn shepherd-btn-primary',
            action: function (this: any) {
              this.complete();
              zone.run(() => void router.navigate([`/${APP_ROUTES.DASHBOARD}`]));
            }
          },
          {
            text: 'Finish here',
            classes: 'shepherd-btn shepherd-btn-outline',
            action: function (this: any) {
              this.complete();
            }
          }
        ]
      }
    ];
  }
}
